// Live Status card, grouped layer controls, and incident features.
//
// Performance note, since a slow first paint is the thing that loses users:
// the status card is ONE request covering every provider, not one per layer.
// Nine parallel calls on a phone is how a page ends up feeling slow even when
// each call is fast.

import { isFeedDown } from './feed-health.ts';
import {
  TANK_LITRES, cheapestFuel, tankCost, type FuelGrade, type FuelGrades,
} from './fuel-grade.ts';
import { PALETTE } from './icons.ts';
import { layerIcon } from './layer-icons.ts';
import { accentColourFor, statusColourFor } from './map-style.ts';
import { SOURCE_NOTES } from './sources.ts';

import { deviceMapsApp, directionsLink } from './directions.ts';
import { escapeHtml } from './escape.ts';
import { BASE, getJson } from './http.ts';

export type Severity = 'ok' | 'info' | 'minor' | 'major' | 'unknown';

export interface StatusItem {
  providerId: string;
  label: string;
  group: string;
  severity: Severity;
  summary: string;
  count: number;
  /** Show in the Now box even when not an alert — see the backend type. */
  pinned?: boolean;
  /** The specific feature this line should open, rather than the first one. */
  focusId?: string;
  /** The features the line counted, when it counted several — see the backend type. */
  listIds?: string[];
  configured: boolean;
  updatedAt: number;
  error: string;
}

export interface TimelineEntry { at: number; text: string; }

export interface LiveFeature {
  /**
   * Sprite the map draws for this feature. Either instead of a coloured circle,
   * where the feature is an object rather than an event — a rig, a wave buoy —
   * or on top of one, where the circle carries the severity and the glyph says
   * what kind of thing it is, as power cuts do. Names match icons.ts.
   */
  readonly icon?: string;
  /**
   * How big this is in the provider's own units — customers off supply, for
   * power. Drives the circle radius so a fault affecting a street and one
   * affecting a town do not draw identically.
   */
  readonly scale?: number;
  /** Tides only: the station whose curve this card should fetch. */
  readonly chartStationId?: string;
  /**
   * Cameras only: the mirrored frame's path on the API host — prefix BASE.
   * Absent when the camera is in the index but its JPEG is not in the mirror.
   */
  readonly imageUrl?: string;
  /**
   * Cameras only: every camera on this gantry, when there is more than one.
   * Traffic Scotland pair them up and down the road at one grid reference —
   * see the backend type. Absent for the single-camera case.
   */
  readonly views?: { label: string; imageUrl: string; imageAgeMs?: number }[];
  /** Cameras only: how old the mirrored frame is, in ms — the fade thresholds on it. */
  readonly imageAgeMs?: number;
  /** Cameras only: where the camera looks — "Northbound, towards Inverurie". */
  readonly direction?: string;
  /** Cameras only: road number parsed from the name, for control-room grouping. */
  readonly road?: string;
  /**
   * The publisher's own level, where they publish one — "YELLOW", "AMBER".
   *
   * Distinct from `severity`, which is our five-value scale and cannot tell
   * amber from red. See zoneColour() in map-style.ts.
   */
  readonly level?: string;
  id: string;
  providerId: string;
  title: string;
  status: string;
  severity: Severity;
  lat: number;
  lon: number;
  area: unknown | null;
  startedAt: number | null;
  expectedEndAt: number | null;
  updatedAt: number;
  /** Card rows. `href` makes the value a link — see the backend's own note. */
  details: { label: string; value: string; href?: string }[];
  /** Fuel only: price, label and band per grade — see the backend type. */
  readonly grades?: FuelGrades;
  timeline: TimelineEntry[];
  source: string;
  sourceUrl: string;
}


/**
 * The Now box's fuel line, expanded: the ten cheapest forecourts for petrol
 * and for diesel, side by side in one panel. Rows use the incident-row shape,
 * so clicking one reveals the layer and opens that forecourt's card.
 */
export function renderFuelTop(el: HTMLElement, features: readonly LiveFeature[], n = 10): void {
  const section = (grade: FuelGrade, heading: string): string => {
    const top = cheapestFuel(features, grade, n);
    const rows = top.map((f, i) => {
      const g = f.grades![grade]!;
      const where = f.details.find((d) => d.label === 'Postcode')?.value ?? '';
      return `
      <li class="mv inc-row" data-incident="${escapeHtml(f.id)}" data-grade="${grade}">
        <span class="mv-arrow">${i + 1}.</span>
        <span class="mv-main">
          <span class="mv-callsign">${escapeHtml(f.title)}</span>
          ${where ? `<span class="mv-detail">${escapeHtml(where)}</span>` : ''}
        </span>
        <span class="mv-state fuel-price">
          ${escapeHtml(g.label)}
          <span class="fuel-tank">${escapeHtml(tankCost(g.price))} / ${TANK_LITRES} L</span>
        </span>
      </li>`;
    }).join('');
    return `
      <h3 class="fuel-top-head">${heading}</h3>
      ${top.length ? `<ul class="ap-list">${rows}</ul>`
        : '<p class="ap-empty">No prices published.</p>'}`;
  };

  el.innerHTML = `
    <div class="ap-head">
      <h2>Cheapest fuel</h2>
      <button id="ap-close" aria-label="Close">×</button>
    </div>
    ${section('petrol', '⛽ Unleaded (E10)')}
    ${section('diesel', '⛽ Diesel')}
    <p class="ap-ops">Tank costs are indicative: ${TANK_LITRES} litres from empty at the pump price.</p>`;
}

export interface LayerGroup {
  group: string;
  layers: {
    id: string; label: string; icon: string;
    enabled: boolean; configured: boolean;
    /**
     * Why this row is not offering what the reader expects.
     *
     * Two cases, and the row treats them differently on purpose. For an
     * unconfigured layer it is why the layer is unselectable, and the row is
     * greyed and disabled. For a *configured* one it is why the layer is
     * empty here — the backend sets it only when empty is not the ordinary
     * quiet case — and the row stays fully selectable, because the condition
     * can clear on the next poll without anybody doing anything. Greying that
     * would lock a reader out of a layer that had quietly started working.
     *
     * Absent on older backends.
     */
    note?: string;
  }[];
}

export const fetchStatus = () => getJson<{ generatedAt: number; items: StatusItem[] }>('/status');
export const fetchLayers = () => getJson<{ groups: LayerGroup[] }>('/layers');
export const fetchLiveFeatures = () => getJson<{ features: LiveFeature[] }>('/live');

/**
 * Shapes for the features that have one, keyed by feature id.
 *
 * Fetched once at load rather than with the features, because these change
 * when a road is rebuilt and the numbers drawn on them change every five
 * minutes. Sending them together would put every metre of the A90 on the
 * twenty-second poll — see the /geometry route for the full argument.
 *
 * **The place goes in the URL, and it is not decoration.** The response is
 * cached hard — it is geometry, it changes when a road is rebuilt — and a
 * browser caches by URL. In production each city has its own API hostname so
 * the URLs differ anyway. Locally they do not: `buildabdn` and `buildedin`
 * both serve `localhost:3000`, so Edinburgh's page was handed Aberdeen's
 * cached geometry, matched none of Edinburgh's feature ids, and drew no
 * coloured roads at all. Not a few — none, which is what made it look like the
 * layer had broken rather than the cache being stale.
 *
 * `place` comes from /meta, so it is the backend's own answer to which city it
 * is serving rather than anything the page assumed.
 */
export const fetchGeometry = (place: string) =>
  getJson<{
    lines: Record<string, [number, number][]>;
    /** Filled boundaries — Low Emission Zones today. Absent on older backends. */
    areas?: Record<string, { type: 'Polygon' | 'MultiPolygon'; coordinates: unknown }>;
  }>(
    `/geometry${place ? `?place=${encodeURIComponent(place)}` : ''}`,
  );

/** The spec's traffic lights, in one place so card and map agree. */
export const SEVERITY_COLOUR: Record<Severity, string> = {
  major: '#ff453a',
  minor: '#ff9f0a',
  info: '#4cc2ff',
  ok: '#32d74b',
  unknown: '#6b7887',
};

const DOT: Record<Severity, string> = {
  major: '🔴', minor: '🟠', info: '🔵', ok: '🟢', unknown: '⚪',
};

// ── Live Status card ────────────────────────────────────────────────────────

/**
 * The card's header, with the control that folds it away.
 *
 * The Now card is the one panel with no console button and no ×, because it is
 * the answer to the question the site exists to answer and dismissing it
 * permanently is not a state worth offering. On a phone it is also a third of
 * the screen, parked above the console, in front of the map — and when what you
 * want is the map, a third of the screen spent on three lines you have already
 * read is the wrong trade. So it collapses to its title bar and comes back with
 * one tap, which is the middle setting between always and never.
 *
 * The chevron's glyph and aria-expanded are set after render, from the stored
 * state, for the reason boxHead gives: the card re-renders every sixty seconds
 * and knows nothing about being folded.
 *
 * #st-time is the freshness reading, filled by main.ts — it used to be a pill
 * in the bottom-right corner and now sits beside the word it qualifies. This
 * function once took a `time` flag that the no-alerts caller passed false to,
 * which hid the reading exactly when the map looked emptiest and a reader most
 * wants to know it is still listening: "nothing is happening" is a claim about
 * a moment, and it is worth nothing without the moment. Both variants carry it
 * now, so the flag had nothing left to decide.
 */
function statusHead(): string {
  return `
    <div class="st-head">
      <h2>Now</h2>
      <div class="st-tools">
        <span id="st-time"></span>
        <button class="st-collapse" data-collapse-status aria-expanded="true"
                aria-label="Collapse Now">&#9662;</button>
      </div>
    </div>`;
}

/**
 * The public card shows active conditions only — a power cut, a flood warning,
 * a gale. Setup diagnostics ("Voi key not set", "Camera mirror not configured")
 * were previously listed here in grey; they are operational detail, and the
 * hidden /status.html page already presents them properly with last-update
 * times and error columns. So they are filtered out here rather than removed
 * from /api/v1/status, which keeps that page working.
 */
export function isActiveAlert(i: StatusItem): boolean {
  if (!i.configured) return false;
  if (i.severity === 'unknown') return false;
  // 'ok' rows are reassurance, not alerts — but a green line for something the
  // user asked about is worth keeping, so they are shown only when nothing is
  // wrong (handled by the caller falling back to the plain message).
  return i.severity === 'major' || i.severity === 'minor';
}

export function renderStatusCard(el: HTMLElement, items: StatusItem[]): void {
  const active = items.filter(isActiveAlert);
  // Pinned lines are not alerts and are shown regardless — the cheapest fuel in
  // town is worth reading on a quiet day, which is exactly when an alerts-only
  // card is empty.
  const pinned = items.filter((i) => i.pinned && i.configured && i.count > 0
    && !active.includes(i));

  if (active.length === 0 && pinned.length === 0) {
    // Say plainly when there is nothing, rather than rendering an empty box
    // that reads as a fault.
    el.innerHTML = `
      ${statusHead()}
      <p class="st-none">🟢 No active alerts right now</p>`;
    return;
  }

  const row = (i: StatusItem) => `
    <li class="st${i.severity === 'major' ? ' st-critical' : ''}"
        data-provider="${escapeHtml(i.providerId)}"
        ${i.focusId ? `data-focus="${escapeHtml(i.focusId)}"` : ''}
        ${i.listIds?.length ? `data-list="${escapeHtml(i.listIds.join('|'))}"` : ''}
        ${i.count > 0 ? 'data-clickable="1"' : ''}>
      <span class="st-dot">${DOT[i.severity] ?? '⚪'}</span>
      <span class="st-text">${escapeHtml(i.summary)}</span>
      ${i.count > 0 ? '<span class="st-go">›</span>' : ''}
    </li>`;

  // Severity first, then the pinned lines.
  //
  // A coloured dot alone was doing all the work of separating "the A90 is shut"
  // from "unleaded is 3p cheaper at Costco", and on a phone, in daylight, at a
  // glance, it was not enough — the rows are the same weight and the same
  // colour, so the eye finds the top one rather than the worst one. Major
  // alerts now sort above minor ones and are marked for the stylesheet to
  // emphasise.
  //
  // Sorted rather than filtered: a minor alert is still worth showing, it is
  // simply not the thing to read first.
  const bySeverity = [...active].sort((a, b) =>
    (b.severity === 'major' ? 1 : 0) - (a.severity === 'major' ? 1 : 0));

  const rows = [...bySeverity.map(row), ...pinned.map(row)].join('');

  const reassurance = active.length === 0
    ? '<p class="st-none">🟢 No active alerts right now</p>'
    : '';

  el.innerHTML = `
    ${statusHead()}
    ${reassurance}
    <ul class="st-list">${rows}</ul>`;
}

/**
 * Every feature behind one status line, worst first.
 *
 * The Live Status card says "2 power outages · 3 customers" and used to open
 * one of them — whichever the provider named, or whichever sorted first. That
 * is right when there is one and wrong when there are several: the line counts
 * them, so clicking it should show them, and the reader picks.
 *
 * Same shape as the rail disruption list, and for the same reason: the count is
 * the subject, so the list is the answer.
 *
 * Ordered by severity, matching the map's rule that the most serious thing is
 * the one you see first.
 */
export function renderFeatureList(
  el: HTMLElement,
  title: string,
  features: readonly LiveFeature[],
): void {
  const rank: Record<Severity, number> = { major: 0, minor: 1, info: 2, unknown: 3, ok: 4 };
  const sorted = [...features].sort((a, b) => rank[a.severity] - rank[b.severity]);

  if (sorted.length === 0) {
    el.innerHTML = `
      <div class="ap-head"><h2>${escapeHtml(title)}</h2><button id="ap-close" aria-label="Close">×</button></div>
      <p class="ap-empty">Nothing to show — it may have cleared.</p>`;
    return;
  }

  const rows = sorted.map((f) => `
    <li class="mv inc-row" data-incident="${escapeHtml(f.id)}">
      <span class="mv-arrow">${DOT[f.severity] ?? '⚪'}</span>
      <span class="mv-main">
        <span class="mv-callsign">${escapeHtml(f.title)}</span>
        <span class="mv-detail">${escapeHtml(f.status)}</span>
      </span>
      <span class="mv-state">›</span>
    </li>`).join('');

  el.innerHTML = `
    <div class="ap-head">
      <h2>${escapeHtml(title)} <span>${sorted.length}</span></h2>
      <button id="ap-close" aria-label="Close">×</button>
    </div>
    <ul class="ap-list">${rows}</ul>`;
}

// ── Layer controls, grouped ─────────────────────────────────────────────────

const GROUP_LABEL: Record<string, string> = {
  utilities: 'Utilities',
  transport: 'Transport',
  // "Trunk roads" until the register arrived (#59): every row was Traffic
  // Scotland's trunk network, and the heading said so. A row called Local
  // roadworks under a heading called Trunk roads contradicts itself, so the
  // heading is the group and the rows say which roads they cover.
  roads: 'Roads',
  environment: 'Environment',
  culture: 'Festivals',
  events: 'Events',
};

/**
 * A layer's glyph, preferring the designed SVG.
 *
 * The backend still sends an emoji per provider, and that is the fallback: a
 * provider added tomorrow appears with a sensible icon instead of a gap, and
 * nothing has to be changed in two places to ship it. The SVG is trusted markup
 * from our own module, so it goes in unescaped; the emoji is escaped because it
 * arrives over the wire.
 */
function rowIcon(id: string, fallback: string): string {
  return `<span class="lc-icon">${layerIcon(id) || escapeHtml(fallback)}</span>`;
}

/**
 * Which status line speaks for which vehicle layer.
 *
 * The vehicle layers are named for what they show and the status lines for the
 * feed behind them, and the two do not always match: trains come from the rail
 * board. Only the differences need listing.
 */
const STATUS_FOR_KIND: Record<string, string> = { trains: 'rail' };

/**
 * Grey out and lock any layer whose feed is not delivering.
 *
 * A checkbox that does nothing is worse than one that is missing: the reader
 * ticks it, sees an empty map, and concludes the map is broken rather than the
 * source. This is the same treatment already given to layers with no source
 * wired up — greyed, disabled, with a sentence saying why — extended to a
 * source that is simply down at the moment.
 *
 * Deliberately not hiding the row. "Where did the ships go?" is a worse
 * question than "why are the ships unavailable?", and the answer to the second
 * is right there in the tooltip.
 *
 * The checked state is left exactly as it was, so a reader who had the layer on
 * gets it back when the feed returns, without having to notice and re-tick it.
 */
export function applyLayerHealth(
  roots: readonly HTMLElement[],
  items: readonly {
    providerId: string; configured: boolean; summary: string;
    updatedAt: number; error: string;
  }[],
): void {
  const byId = new Map(items.map((i) => [i.providerId, i]));

  for (const root of roots) {
    for (const row of root.querySelectorAll<HTMLElement>('.lc-row')) {
      const input = row.querySelector<HTMLInputElement>('input[type=checkbox]');
      if (!input) continue;

      const id = input.dataset.provider
        ?? (input.dataset.kind ? STATUS_FOR_KIND[input.dataset.kind] ?? input.dataset.kind : '');
      const status = id ? byId.get(id) : undefined;
      if (!status) continue;

      // A layer with no source at all keeps its own wording; this is only about
      // a source that exists and is not answering.
      if (row.classList.contains('lc-pending')) continue;

      const down = isFeedDown(status);
      row.classList.toggle('lc-down', down);
      input.disabled = down;
      if (down) {
        // "locked" rather than "unavailable", because some providers already
        // say unavailable in their summary and "Fuel prices unavailable —
        // unavailable until the feed returns" is not a sentence. It also
        // describes what actually happened to the control.
        row.title = `${status.summary} — locked until the feed returns`;
      } else if (row.title.endsWith('until the feed returns')) {
        row.title = '';
      }
    }
  }
}

function providerRow(l: LayerGroup['layers'][number], count?: number): string {
  // A note on a configured layer says why it is empty, not why it is
  // unavailable, and it opens on hover rather than taking a line of its own.
  //
  // It was an inline line first, and one look at the rendered box settled it:
  // the sentence wrapped to three lines and turned one row of six into half
  // the panel, which reads as an error state for a layer working exactly as
  // designed. The second attempt put it in the row's `title`, which was worse
  // in a quieter way — a native tooltip needs a second of stillness, renders
  // as an OS chrome box nothing else here uses, and is missed entirely by
  // anyone who does not already suspect it is there.
  //
  // So it is a real panel, drawn by this stylesheet, opening immediately on
  // hover and on keyboard focus, with the label dotted to say it exists. No
  // `title`, deliberately: both would open and the browser's would sit on top
  // of ours.
  const why = l.configured ? (l.note ?? '') : '';
  return `
    <label class="lc-row ${l.configured ? '' : 'lc-pending'}${why ? ' lc-noted' : ''}"
           title="${l.configured ? '' : escapeHtml(l.note || 'No data source wired up yet')}">
      <input type="checkbox" data-provider="${escapeHtml(l.id)}"
             ${l.enabled && l.configured ? 'checked' : ''} ${l.configured ? '' : 'disabled'} />
      ${rowIcon(l.id, l.icon)}
      <span class="lc-label">${escapeHtml(l.label)}</span>
      <span class="count" id="count-${escapeHtml(l.id)}">${count ?? '–'}</span>
      ${why ? `<span class="lc-tip" role="note">${escapeHtml(why)}</span>` : ''}
    </label>`;
}

/**
 * Transport box: the three vehicle layers plus any transport-group providers.
 *
 * Voi bikes arrive from the backend in a `transport` group, and used to render
 * in a second accordion also titled "Transport" — two identical headings on
 * screen. They now share one box with the vehicle rows.
 */
export function renderTransportBox(
  el: HTMLElement,
  groups: LayerGroup[],
  vehicleLayers: { kind: string; label: string }[],
  state: Record<string, boolean>,
  /** Tram stops in this place's bake — zero renders no tram row at all. */
  tramStops = 0,
  /** Subway stations in the bake — Glasgow by the data, same rule as trams. */
  subwayStops = 0,
  /** Which grade the fuel layer is drawing, for the switch under its row. */
  fuelGrade: FuelGrade = 'petrol',
): void {
  // The glyph replaces the colour swatch that used to tie a row to its map
  // marker. It still does that job — each icon carries its layer's colour — and
  // it says which layer as well, which a square never did.
  const vehicleRows = vehicleLayers.map((l) => `
    <label class="lc-row">
      <input type="checkbox" data-kind="${l.kind}" ${state[l.kind] !== false ? 'checked' : ''} />
      ${rowIcon(l.kind, '')}
      <span class="lc-label">${escapeHtml(l.label)}</span>
      <span class="count" id="count-${l.kind}">–</span>
    </label>`).join('');

  // Fuel gets a Petrol / Diesel switch under its row. Radios rather than a
  // second checkbox, and outside `.lc-row`, so applyLayerHealth and the
  // selection code — both of which read every checkbox in the box — never
  // mistake it for a layer.
  const gradeSwitch = (): string => `
    <div class="fuel-grade" role="radiogroup" aria-label="Fuel price shown on the map">
      ${(['petrol', 'diesel'] as const).map((g) => `
        <label class="fuel-grade-opt">
          <input type="radio" name="fuel-grade" data-fuel-grade="${g}"
                 ${fuelGrade === g ? 'checked' : ''} />
          <span>${g === 'petrol' ? 'Petrol' : 'Diesel'}</span>
        </label>`).join('')}
    </div>`;

  const transportProviders = (groups.find((g) => g.group === 'transport')?.layers ?? [])
    .map((l) => providerRow(l) + (l.id === 'fuel' && l.configured ? gradeSwitch() : '')).join('');

  // Off by default and loaded on demand: 1,481 stops is ~100 kB nobody should
  // pay for unless they want it. Labelled "timetable" because that is what it
  // is — these boards show booked times, not tracked buses.
  const stopsRow = `
    <label class="lc-row" title="Scheduled departures from the published timetable">
      <input type="checkbox" data-stops />
      ${rowIcon('stops', '')}
      <span class="lc-label">Bus stops (timetable)</span>
      <span class="count" id="count-stops">–</span>
    </label>`;

  // A row only where the bake has tram platforms — Edinburgh by the data,
  // never by naming the place. Same deferred payload as the bus stops; the
  // two toggles split one fetch.
  const tramRow = tramStops > 0 ? `
    <label class="lc-row" title="Scheduled tram departures from the published timetable">
      <input type="checkbox" data-trams />
      ${rowIcon('tram-stops', '')}
      <span class="lc-label">Tram stops (timetable)</span>
      <span class="count" id="count-trams">–</span>
    </label>` : '';

  // The same rule again: a row only where the bake found the mode. "Stations"
  // rather than "stops" for the Subway, because that is what the merged marks
  // are — one per station, both circles on its board. No ferry row: the
  // terminal is the ⚓ Ferries pin, and its sailings live in that panel.
  const subwayRow = subwayStops > 0 ? `
    <label class="lc-row" title="Scheduled Subway departures from the published timetable">
      <input type="checkbox" data-subways />
      ${rowIcon('subway-stops', '')}
      <span class="lc-label">Subway stations (timetable)</span>
      <span class="count" id="count-subways">–</span>
    </label>` : '';

  el.innerHTML = boxHead('Transport', 'transport') + vehicleRows + stopsRow + tramRow
    + subwayRow + transportProviders;
}

/**
 * Everything that is not transport, roads, events or weather.
 *
 * The named group's own rows sit directly under the box title — nesting them
 * in an accordion inside a box already carrying that heading repeats the
 * duplicate-heading problem this rework exists to remove. Whatever is left
 * over stays as collapsed accordions, since it is secondary.
 *
 * Environment is the named group and Utilities the accordion, which is the
 * reverse of how this box shipped. The counts settled it: eight environment
 * layers against two utilities, so the box was headed by its smaller half and
 * a reader after a weather warning had to know to expand something labelled
 * for power cuts. Water and power have not gone anywhere — they are one click
 * down rather than one click up.
 */
/**
 * Which backend groups the Events box claims.
 *
 * Two names for one box, because the thing is outgrowing the name it was born
 * with. `culture` is what the Fringe provider has always sent; `events` is for
 * what comes next — a match at Pittodrie, a concert at P&J Live — which is the
 * same question asked of the map ("something big is on, what will it do to
 * getting about?") and so belongs behind the same button.
 *
 * Accepting both means a provider added to the backend under either name lands
 * here without a second change over here to remember.
 */
export const EVENT_GROUPS = new Set(['culture', 'events']);

/**
 * Groups that have a box of their own, and so must not also be folded into
 * the Environment box as a collapsed section.
 *
 * Environment is in here because it heads that box directly rather than
 * despite having no box: listing it would draw every environment layer twice,
 * once under the title and once inside an "Environment" accordion beneath it.
 *
 * Roads earned one: Traffic Scotland publish incidents, roadworks, journey
 * times, signs and cameras, which is five toggles about driving buried under a
 * heading about power cuts. Utilities was becoming the box for everything that
 * was not transport or weather.
 *
 * Events earned one for the opposite reason — not that it is big now, but that
 * it is about to be. A football crowd or a concert emptying out is a load on
 * the roads and the buses, which is the subject of this map rather than a
 * footnote to power cuts; leaving it as a collapsed accordion inside this box
 * would mean the traffic story nobody can find is the one that moves 20,000
 * people at once.
 */
const OWN_BOX = new Set(['transport', 'environment', 'roads', ...EVENT_GROUPS]);

export function renderEnvironmentBox(el: HTMLElement, groups: LayerGroup[]): void {
  const primary = groups.find((g) => g.group === 'environment')?.layers ?? [];
  const secondary = groups.filter((g) => !OWN_BOX.has(g.group));

  el.innerHTML = boxHead('Environment', 'environment')
    + primary.map((l) => providerRow(l)).join('')
    + secondary.map((g) => `
      <details class="lc-group">
        <summary>${escapeHtml(GROUP_LABEL[g.group] ?? g.group)}</summary>
        ${g.layers.map((l) => providerRow(l)).join('')}
      </details>`).join('');
}

/**
 * Traffic Scotland's layers, in their own box.
 *
 * Named for the road rather than the publisher. "Roads" is what a reader is
 * looking for; Traffic Scotland is who supplies it, which belongs in the
 * attribution line under the toggles and on the sources page — not as the
 * heading somebody has to recognise before they can find roadworks.
 *
 * The attribution is here rather than only on the sources page because it is a
 * licence condition, and a box that draws their data ought to say so where the
 * data is being switched on.
 *
 * The logo is here for the same reason and one more. Traffic Scotland's policy
 * names it specifically, alongside the acknowledgement and the link — and the
 * only other place it appeared was the sources-page footer, on a page nothing
 * links to from the map. A condition discharged two clicks away and below the
 * fold is thin. This is where their data gets switched on, so this is where
 * their mark belongs.
 *
 * It stays supplementary to the written credit rather than replacing it: `alt`
 * is empty and the sentence carries the attribution alone, so a missing file
 * degrades to a complete credit rather than to a broken image where the
 * acknowledgement should be. The file is not committed — see
 * frontend/public/README.md — which makes that the ordinary case, not the
 * unlucky one.
 *
 * Not itself a link. The words beside it already go to traffic.gov.scot, and a
 * logo that is clickable on its own starts to read as a partner badge, which
 * the same policy does not grant and this site must not imply.
 */
/**
 * What the colours on the trunk road mean.
 *
 * Traffic Scotland's own congestion map paints the network and puts a key
 * beside it, and the key is not optional decoration — a coloured road with no
 * legend is a map asking the reader to guess whether green means "clear" or
 * "roadworks". Three words and three swatches is the whole of it.
 *
 * Rendered whenever the layer exists rather than only when it is switched on.
 * The box re-renders at load and the checkboxes are handled without one, so a
 * key that appeared and disappeared with the tick would need the box to
 * re-render on every toggle — a lot of machinery to hide three words that
 * explain what the tick next to them will do.
 */
function journeyKey(layers: LayerGroup['layers']): string {
  const journey = layers.find((l) => l.id === 'journey-times');
  if (!journey || !journey.configured) return '';

  const swatch = (colour: string, label: string) =>
    `<span class="lc-key-item"><i style="background:${colour}"></i>${label}</span>`;

  return `<p class="lc-key">
      ${swatch(PALETTE.journey, 'Moving')}
      ${swatch(PALETTE.journeySlow, 'Slow')}
      ${swatch(PALETTE.journeyStopped, 'Stopped')}
    </p>`;
}

/**
 * The way into the camera control room, under the Cameras toggle it belongs
 * to. Only when the mirror is configured: a button opening an empty overlay
 * is the broken-checkbox problem wearing a nicer coat. The feed count beside
 * it is filled by the live refresh, the same way the layer counts are.
 */
function controlRoomButton(layers: LayerGroup['layers']): string {
  const cameras = layers.find((l) => l.id === 'cameras');
  if (!cameras?.configured) return '';
  return `<p class="lc-cams">
      <button type="button" id="cr-open">Open control room</button>
      <span id="cr-count"></span>
    </p>`;
}

export function renderRoadsBox(el: HTMLElement, groups: LayerGroup[]): void {
  const layers = groups.find((g) => g.group === 'roads')?.layers ?? [];

  if (layers.length === 0) {
    el.innerHTML = `${boxHead('Roads', 'roads')}<p class="lc-empty">No road data configured.</p>`;
    return;
  }

  el.innerHTML = boxHead('Roads', 'roads')
    + layers.map((l) => providerRow(l)).join('')
    + controlRoomButton(layers)
    + journeyKey(layers)
    + `<p class="lc-credit"><span>Data © Transport Scotland, via
         <a href="https://www.traffic.gov.scot/" target="_blank"
            rel="noopener noreferrer">Traffic Scotland</a></span
       ><img class="lc-credit-logo" src="/logo-ts-blue.svg" alt="" aria-hidden="true"
         width="104" height="26" loading="lazy"
         data-hide-on-error /></p>`;
}

/**
 * What is on: festivals today, match days and concerts next.
 *
 * The rows sit directly under the box title rather than inside a "Festivals"
 * accordion, for the reason the Utilities box gives — a heading inside a box
 * that already carries a heading is one heading too many. The group name is
 * spent here, so a place sending both `culture` and `events` gets one flat list
 * rather than two sections of one row each.
 *
 * Renders nothing at all when the place has no event layers, which today is
 * every place but Edinburgh. `.box:empty` then hides the box (see style.css),
 * and buildDock hides the button, so Aberdeen does not get a fifth glyph in the
 * console that opens an empty panel. An empty box is worse than no box: it
 * reads as a feed that has broken rather than a thing this city does not have.
 */
export function renderEventsBox(el: HTMLElement, groups: LayerGroup[]): void {
  const layers = groups.filter((g) => EVENT_GROUPS.has(g.group)).flatMap((g) => g.layers);

  if (layers.length === 0) {
    el.innerHTML = '';
    return;
  }

  el.innerHTML = boxHead('Events', 'events') + layers.map((l) => providerRow(l)).join('');
}

/**
 * Shared header: the panel's title, its collapse chevron, its dismiss control —
 * and the drag handle, which is the header itself.
 *
 * The chevron's glyph and aria-expanded are set from the panel's stored state
 * after render (see floating.ts), not here, because a box re-renders far more
 * often than it collapses and the renderers have no reason to know about it.
 */
export function boxHead(title: string, id: string): string {
  return `
    <div class="box-head">
      <h2>${escapeHtml(title)}</h2>
      <div class="box-tools">
        <button class="box-collapse" data-collapse="${escapeHtml(id)}" aria-expanded="true"
                aria-label="Collapse ${escapeHtml(title)}">▾</button>
        <button class="box-close" data-close="${escapeHtml(id)}"
                aria-label="Hide ${escapeHtml(title)}">×</button>
      </div>
    </div>`;
}

// ── Incident card ───────────────────────────────────────────────────────────

function when(ms: number | null): string {
  if (!ms) return '';
  return new Date(ms).toLocaleString('en-GB', {
    timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit',
    day: 'numeric', month: 'short',
  });
}

/** FUBAR News brand red, from their logo. */
export const FUBAR_RED = '#ED1B34';

// ── Camera popup ────────────────────────────────────────────────────────────

/**
 * When a mirrored camera frame counts as stale. Traffic Scotland refresh the
 * LEV frames on a 5–10-minute cycle and the box mirrors every five, so a
 * healthy frame can legitimately be ~15 minutes old; thirty is the upstream
 * having missed at least twice over — a camera in operational use, or the
 * mirror having stopped. From here the sprite, the popup image and the
 * control-room tiles all fade — the fade IS the signal, with no badge and no
 * age row, because the frame has its own date and time burned in and
 * restating them was reviewed and rejected. The same number as the backend's
 * CAMERA_STALE_MS; drift between the two would fade the sprite and not the
 * popup, or the other way round.
 */
export const CAMERA_STALE_MS = 30 * 60_000;

/** Great-circle metres, for the nearby search. */
function distanceM(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLon = toRad(bLon - aLon);
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLon / 2) ** 2;
  return 6_371_000 * 2 * Math.asin(Math.sqrt(h));
}

/** Which way something lies from the camera, as an 8-point compass word. */
function compassFrom(aLat: number, aLon: number, bLat: number, bLon: number): string {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLon = toRad(bLon - aLon);
  const y = Math.sin(dLon) * Math.cos(toRad(bLat));
  const x = Math.cos(toRad(aLat)) * Math.sin(toRad(bLat))
    - Math.sin(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.cos(dLon);
  const bearing = ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
  const points = [
    'north', 'north-east', 'east', 'south-east',
    'south', 'south-west', 'west', 'north-west',
  ];
  return points[Math.round(bearing / 45) % 8]!;
}

/** "600 m", to the nearest hundred, or "1 km" at the search radius's edge. */
function roundDistance(m: number): string {
  const rounded = Math.max(100, Math.round(m / 100) * 100);
  return rounded >= 1000 ? '1 km' : `${rounded} m`;
}

/** How far the map searches around a camera for something worth a sentence. */
const NEARBY_M = 1000;

/**
 * One line about the nearest thing the map already knows, plus the journey
 * time for the section the camera sits on.
 *
 * Composed from the already-loaded features, never fetched — the popup must
 * open instantly, and everything needed is in the live payload. Both parts
 * are optional and the block is omitted entirely when nothing is nearby: a
 * popup that always has an amber box teaches people to ignore the amber box.
 */
function nearbyNote(f: LiveFeature, all: readonly LiveFeature[]): { note: string; route: string } {
  const near = (candidate: LiveFeature) =>
    distanceM(f.lat, f.lon, candidate.lat, candidate.lon);

  // Nearest first, then worse severity first; one incident sentence, maximum.
  const rank: Record<Severity, number> = { major: 0, minor: 1, info: 2, unknown: 3, ok: 4 };
  const incident = all
    .filter((x) => (x.providerId === 'incidents' || x.providerId === 'roadworks')
      && near(x) <= NEARBY_M)
    .sort((a, b) => (near(a) - near(b)) || (rank[a.severity] - rank[b.severity]))[0];

  let incidentLine = '';
  if (incident) {
    // "A90 — Road Traffic Collision" → "Road Traffic Collision"; the road is
    // already in the camera's own title, a metre above this line.
    const what = incident.providerId === 'roadworks' ? 'Roadworks'
      : incident.title.split(' — ').pop() || 'Incident';
    const lanes = Number(incident.details.find((d) => d.label === 'Lanes closed')?.value);
    const until = incident.details.find((d) =>
      d.label === 'Expected clearance' || d.label === 'End')?.value ?? '';
    const clause = [
      Number.isFinite(lanes) && lanes > 0 ? `${lanes === 1 ? 'lane' : `${lanes} lanes`} closed` : '',
      until ? `until ${until}` : '',
    ].filter(Boolean).join(' ');
    incidentLine = `${what} ${roundDistance(near(incident))} `
      + `${compassFrom(f.lat, f.lon, incident.lat, incident.lon)}`
      + `${clause ? ` — ${clause}` : ''}.`;
  }

  // The section the camera sits on, when travel-times has one.
  const journey = all
    .filter((x) => x.providerId === 'journey-times' && near(x) <= NEARBY_M)
    .sort((a, b) => near(a) - near(b))[0];
  const delayed = journey && (journey.severity === 'minor' || journey.severity === 'major');
  const diff = journey?.details.find((d) => d.label === 'Difference')?.value ?? '';

  const parts = [incidentLine];
  // A normal section is dropped rather than stated — see the omission rule —
  // so only a real delay earns the second sentence.
  if (journey && delayed && diff) parts.push(`${journey.title} running ${diff}.`);

  // A clear road with no incident is worth one calm green line instead of an
  // amber box that would cry wolf: "Charleston to Bridge of Dee · 6 min".
  let route = '';
  if (!incidentLine && journey && !delayed) {
    const normal = diff === 'Running normally' ? ', normal' : '';
    route = `${journey.title} · ${journey.status}${normal}`;
  }

  return { note: parts.filter(Boolean).join(' '), route };
}

/**
 * The camera popup: the mirrored frame, where the camera looks, one line
 * about anything happening nearby, and the Traffic Scotland link.
 *
 * Three states, all designed: live, stale (the image and sprite fade, and the
 * note slot explains the mirror), and no-image (a hatch placeholder — the pin
 * stays on the map because knowing where a camera is, is still worth
 * something). No timestamp anywhere: the frame carries its own date, time and
 * "Traffic Scotland" burned in by the camera.
 */
function renderCamera(f: LiveFeature, all: readonly LiveFeature[]): string {
  const cameraId = f.details.find((d) => d.label === 'Camera')?.value ?? '';
  const stale = (f.imageAgeMs ?? 0) > CAMERA_STALE_MS;
  const noImage = !f.imageUrl;
  const alt = [f.title, f.direction].filter(Boolean).join(' — ');

  /** One frame, with the credit chip the licence asks for on every image. */
  const frame = (url: string, ageMs: number | undefined, label: string) => {
    const old = (ageMs ?? 0) > CAMERA_STALE_MS;
    return `<div class="pop-media cam${old ? ' stale' : ''}">
        <img class="cam-frame" src="${escapeHtml(BASE + url)}"
             alt="${escapeHtml([alt, label].filter(Boolean).join(' — '))}" loading="lazy" />
        ${label ? `<span class="cam-view">${escapeHtml(label)}</span>` : ''}
        <span class="pop-credit">Traffic Scotland</span>
      </div>`;
  };

  // Both cameras on a gantry, one under the other, each captioned with the
  // way it looks. Traffic Scotland's own map toggles between them, which is
  // worth doing for video and pointless for a still: two frames cost one
  // scroll and answer "which way is it backed up?" without a second click.
  const media = noImage
    ? `<div class="pop-nomedia">
         <div>
           <div class="nm-title">NO IMAGE IN THE MIRROR</div>
           <div class="nm-sub">the camera is in the index, its JPEG is not</div>
         </div>
       </div>`
    : (f.views?.length ?? 0) > 1
      ? f.views!.map((v) => frame(v.imageUrl, v.imageAgeMs, v.label)).join('')
      : frame(f.imageUrl!, f.imageAgeMs, '');

  const rows = [
    `<tr><th>Camera</th><td>${escapeHtml(cameraId)}</td></tr>`,
    ...(noImage ? ['<tr><th>Status</th><td>No image</td></tr>'] : []),
  ].join('');

  // Stale takes the note's slot: the one thing worth saying about a faded
  // picture is why it is faded, and an incident line under a twenty-minute-old
  // frame would invite reading the two together as one moment.
  const { note, route } = stale
    ? {
      note: 'This is the last picture that arrived. Traffic Scotland refresh '
        + 'these frames every five to ten minutes, so the road may look '
        + 'nothing like this now.',
      route: '',
    }
    : nearbyNote(f, all);

  return `
    <div class="pop">
      ${media}
      <strong>${escapeHtml(f.title)}</strong>
      ${f.direction ? `<span>${escapeHtml(f.direction)}</span>` : ''}
      <table class="pop-rows">${rows}</table>
      ${note ? `<div class="pop-nearby">${escapeHtml(note)}</div>` : ''}
      ${route ? `<p class="pop-route clear">${escapeHtml(route)}</p>` : ''}
      <a class="pop-link" href="${escapeHtml(f.sourceUrl)}"
         target="_blank" rel="noopener noreferrer">Traffic Scotland camera page →</a>
      <p class="inc-src">${escapeHtml(f.source)}</p>
    </div>`;
}

/**
 * A sign face, drawn as a sign.
 *
 * Amber on black, one character cell per column, at the grid the sign
 * actually has — the baked table carries maxNumberOfRows and
 * maxNumberOfCharacters per gantry, so this is 3x21 or whatever that unit
 * really is rather than a guess.
 *
 * Quoted rather than summarised, and that is the point of the layer. Every
 * other card here takes somebody's data and decides how to word it; this one
 * shows the operator's own words in the operator's own line breaks. Reflowing
 * "ACCIDENT / EXPECT DELAYS" into a sentence would lose the thing worth
 * having.
 *
 * The lanterns are from the feed, not decoration: textLanternsOn is a real
 * per-unit field, so a sign whose lamps are flashing on the road has them lit
 * here, and one whose lamps are off does not.
 */
function vmsFace(f: LiveFeature): string {
  const text = f.details.find((d) => d.label === 'sign')?.value ?? '';
  const grid = f.details.find((d) => d.label === 'grid')?.value ?? '3x21';
  const lit = f.details.find((d) => d.label === 'lanterns')?.value === 'on';
  const [rows, columns] = grid.split('x').map(Number);

  const lines = text.split('\n');
  // Padded to the sign's real height so a one-line message sits where it sits
  // on the gantry rather than floating in the middle of an empty face.
  const height = Math.max(rows || 3, lines.length);
  const padded = Array.from({ length: height }, (_, i) => lines[i] ?? '');

  return `
    <div class="vms" style="--vms-cols:${columns || 21}">
      <div class="vms-bezel">
        ${lit ? '<span class="vms-lamp on" aria-hidden="true"></span>' : ''}
        <div class="vms-face">
          ${padded.map((line) => `<div class="vms-line">${escapeHtml(line)}</div>`).join('')}
        </div>
        ${lit ? '<span class="vms-lamp on right" aria-hidden="true"></span>' : ''}
      </div>
    </div>`;
}

export function renderIncident(f: LiveFeature, all: readonly LiveFeature[] = []): string {
  // Cameras have a designed card of their own — the image is the card — and
  // it reads the whole feature list so the nearby note can say what the map
  // already knows about the road the camera watches.
  if (f.providerId === 'cameras') return renderCamera(f, all);

  // The face carries the message; the rows below it carry the metadata. The
  // three fields that feed the face are not repeated as table rows.
  const hidden = new Set(['sign', 'grid', 'lanterns']);
  // A row carrying an href links its value. `noopener noreferrer` and the ↗ are
  // the same treatment the FUBAR headline gets: the reader is told it leaves the
  // site before they tap it, and the destination is handed no window reference.
  // The URL is escaped like any other value — it came over the wire, and the
  // provider that sent it has already refused anything but http(s).
  const rows = f.details
    .filter((d) => !hidden.has(d.label))
    .map((d) => {
      const value = d.href
        ? `<a href="${escapeHtml(d.href)}" target="_blank" rel="noopener noreferrer">${
          escapeHtml(d.value)} ↗</a>`
        : escapeHtml(d.value);
      return `<tr><th>${escapeHtml(d.label)}</th><td>${value}</td></tr>`;
    })
    .join('');

  if (f.providerId === 'vms') {
    return `
      <div class="inc">
        <div class="inc-head" style="border-color:${SEVERITY_COLOUR[f.severity]}">
          <strong>${escapeHtml(f.title)}</strong>
        </div>
        ${vmsFace(f)}
        ${rows ? `<table class="pop-rows">${rows}</table>` : ''}
        <p class="inc-src">${escapeHtml(f.source)}</p>
      </div>`;
  }

  // Editorial content gets a clearly branded card with the headline linking
  // straight to the source post. It sits on the same map as SSEN outage data,
  // so it must never be mistakable for an official feed.
  if (f.providerId === 'fubar') {
    return `
      <div class="inc fubar">
        <div class="fubar-head">
          <img src="/fubar-icon.png" alt="" width="26" height="26"
               data-hide-on-error />
          <span>FUBAR News</span>
        </div>
        <a class="fubar-title" href="${escapeHtml(f.sourceUrl)}"
           target="_blank" rel="noopener noreferrer">${escapeHtml(f.title)} ↗</a>
        ${rows ? `<table class="pop-rows">${rows}</table>` : ''}
        <p class="inc-src">Reported by FUBAR News · not an official source</p>
      </div>`;
  }

  // Timeline is the part that turns a status into a story — when it was
  // logged, when someone got there, when power is expected back.
  const timeline = f.timeline.length
    ? `<ol class="tl">${f.timeline.map((t) => `
        <li><span class="tl-time">${new Date(t.at).toLocaleTimeString('en-GB',
    { timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit' })}</span>
        <span class="tl-text">${escapeHtml(t.text)}</span></li>`).join('')}</ol>`
    : '';

  // River gauges get a level chart, filled in after the card opens — see
  // onIncidentOpen in map.ts. The container carries the SEPA series id so the
  // fetch does not have to re-derive it from the feature id downstream.
  // Tides get a curve, filled in the same way and for the same reason.
  const tideChart = f.chartStationId
    ? `<div class="tide-chart-slot" data-station="${escapeHtml(f.chartStationId)}">
         <p class="rc-empty">Loading tide curve…</p>
       </div>`
    : '';

  const chart = f.providerId === 'rivers'
    ? `<div class="river-chart" data-ts="${escapeHtml(f.id.replace(/^river:/, ''))}">
         <div class="rc-tabs" role="group" aria-label="Chart period">
           <button type="button" data-w="4h">4h</button>
           <button type="button" data-w="24h" class="on">24h</button>
           <button type="button" data-w="7d">7d</button>
         </div>
         <div class="rc-body"><p class="rc-empty">Loading levels…</p></div>
       </div>`
    : '';

  // Fuel only: the reader is looking at a price because they mean to go there.
  const directions = f.providerId === 'fuel'
    ? directionsLink(f.lat, f.lon, deviceMapsApp())
    : '';

  return `
    <div class="inc">
      <div class="inc-head" style="border-color:${accentColourFor(f.level, SEVERITY_COLOUR[f.severity])}">
        <strong>${escapeHtml(f.title)}</strong>
        <span class="inc-status" style="color:${statusColourFor(f.level, SEVERITY_COLOUR[f.severity])}">${escapeHtml(f.status)}</span>
      </div>
      ${rows ? `<table class="pop-rows">${rows}</table>` : ''}
      ${tideChart}
      ${chart}
      ${timeline}
      ${directions}
      <p class="inc-src">
        ${f.startedAt ? `Since ${escapeHtml(when(f.startedAt))} · ` : ''}
        <a href="${escapeHtml(f.sourceUrl)}" target="_blank" rel="noopener noreferrer"
           title="${escapeHtml(SOURCE_NOTES[f.providerId]?.caveat ?? '')}">${escapeHtml(f.source)} ↗</a>
      </p>
    </div>`;
}
